import { useTranslation } from 'react-i18next';

import { Form } from '@arco-design/web-react';

import { CapacitySlider, CapacitySliderRow, LARGE_RANGE_SLIDER_MARK_COUNT } from '@/components/CapacitySlider';

import {
  DEFAULT_SHARED_MEMORY_SIZE_MARK_COUNT,
  DEFAULT_SHARED_MEMORY_SIZE_MAX,
  DEFAULT_SHARED_MEMORY_SIZE_MIN,
  SHARED_MEMORY_SIZE_UNIT,
} from './constants';
import { SHARED_MEMORY_FIELD } from './fieldKeys';
import styles from './index.module.less';
import { formatSharedMemorySizeGi, parseSharedMemorySizeGi } from './sharedMemoryForm';
import type { SharedMemoryBlockProps } from './types';

interface SharedMemorySizeSliderProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  markCount?: number;
}

/** 共享内存容量滑条：表单存 sizeLimit 字符串（如 2Gi），交互对齐内存配置弹窗 */
function SharedMemorySizeSlider({
  value,
  onChange,
  disabled,
  min = DEFAULT_SHARED_MEMORY_SIZE_MIN,
  max = DEFAULT_SHARED_MEMORY_SIZE_MAX,
  markCount = DEFAULT_SHARED_MEMORY_SIZE_MARK_COUNT,
}: SharedMemorySizeSliderProps) {
  const gi = parseSharedMemorySizeGi(value);

  return (
    <CapacitySlider
      value={gi}
      min={min}
      max={max}
      markCount={markCount ?? LARGE_RANGE_SLIDER_MARK_COUNT}
      translateUnit={false}
      unit={SHARED_MEMORY_SIZE_UNIT}
      disabled={disabled}
      readOnly={disabled}
      onChange={(next) => onChange?.(formatSharedMemorySizeGi(Number(next)))}
    />
  );
}

/** 共享内存配置能力块（启用态由外层 CheckExpandable / 模块勾选控制） */
export function SharedMemoryBlock({ paths, blockMode = 'edit', min }: SharedMemoryBlockProps) {
  const { t } = useTranslation(['data-center', 'cluster']);
  const readonly = blockMode === 'preview';
  const configPath = paths?.config || SHARED_MEMORY_FIELD;
  const sizeLabel = t('data-center:shared.memory.size');
  const resolvedMin = min && min > 0 ? min : DEFAULT_SHARED_MEMORY_SIZE_MIN;

  const checkSharedMemoryLimit = (value: string | undefined, cb: (error?: string) => void) => {
    if (!String(value ?? '').trim()) {
      cb(t('cluster:required', { content: sizeLabel }));
      return;
    }
    const gi = parseSharedMemorySizeGi(value);
    if (gi < resolvedMin) {
      cb(t('data-center:shared.memory.min.value', { min: resolvedMin }));
      return;
    }
    if (gi > DEFAULT_SHARED_MEMORY_SIZE_MAX) {
      cb(`${t('cluster:limitlarge')}${DEFAULT_SHARED_MEMORY_SIZE_MAX}`);
      return;
    }
    cb();
  };

  return (
    <CapacitySliderRow className={styles.memoryRow} unit={SHARED_MEMORY_SIZE_UNIT}>
      <Form.Item
        label={sizeLabel}
        field={`${configPath}.sizeLimit`}
        rules={readonly ? undefined : [{ required: true, validator: checkSharedMemoryLimit }]}
      >
        <SharedMemorySizeSlider disabled={readonly} min={resolvedMin} />
      </Form.Item>
    </CapacitySliderRow>
  );
}
